/**
 * Theme store — preferința de apariție (light/dark/system), persistată în
 * localStorage. Nu e sensibil: e pur UI preference.
 */
import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'cerbes-theme';

function read(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

function resolve(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }
  return mode;
}

let transitionTimeout: number | null = null;

function apply(mode: ThemeMode) {
  const resolved = resolve(mode);
  const html = document.documentElement;
  // Adaugă clasa de tranziție smooth doar pe durata schimbării (300ms)
  // ca să avem fade între light↔dark fără overhead permanent.
  html.classList.add('theme-transition');
  if (resolved === 'dark') html.classList.add('dark');
  else html.classList.remove('dark');
  if (transitionTimeout !== null) window.clearTimeout(transitionTimeout);
  transitionTimeout = window.setTimeout(() => {
    html.classList.remove('theme-transition');
    transitionTimeout = null;
  }, 300);
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  initialize: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: read(),
  setMode: (mode) => {
    localStorage.setItem(STORAGE_KEY, mode);
    set({ mode });
    apply(mode);
  },
  initialize: () => {
    apply(get().mode);
    // Re-aply când user-ul schimbă OS-level preference (doar când mode=system)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', () => {
        if (get().mode === 'system') apply('system');
      });
    }
  },
}));
